import { isTruthy } from '../filter';
import { FilterSelectEvent } from './filter-select-event';

type FormElementNodeList = NodeListOf<HTMLSelectElement | HTMLInputElement>;

const HISTORY_STATE_KEY = 'cross-page-person-list-jump-state';

/**
 * Add additional state to the given {@link previousHistoryState} for cross page person lists.
 */
const extendedState = (
  listIdentifier: string,
  listState: Record<string, string>,
  previousHistoryState = window.history.state
) => {
  const state =
    previousHistoryState !== null ? { ...previousHistoryState } : {};
  if (!(HISTORY_STATE_KEY in state)) {
    state[HISTORY_STATE_KEY] = {};
  }
  state[HISTORY_STATE_KEY][listIdentifier] = listState;
  return state;
};

/**
 * Read the state from history and override the {@link formElement} with their previous values,
 * if they're still holding initial values.
 */
const restoreStateFromHistory = (
  formElements: FormElementNodeList,
  elementIdentifier: string
) => {
  const hasWindowHistoryState =
    window.history.state !== null && HISTORY_STATE_KEY in window.history.state;

  if (!hasWindowHistoryState) {
    return;
  }

  /**
   * The whole history related to the cross-page-list functionality.
   */
  const wholeHistory = window.history.state[HISTORY_STATE_KEY];

  if (
    typeof wholeHistory !== 'object' ||
    wholeHistory === null ||
    !(elementIdentifier in wholeHistory)
  ) {
    return;
  }

  /**
   * The history related to this specific cross-page-list element.
   */
  const elementHistory = wholeHistory[elementIdentifier];

  if (typeof elementHistory !== 'object' || elementHistory === null) {
    return;
  }

  [...formElements]
    // ensure that the user hasn't selected something yet.
    // Can you call it a race condition, if you're racing against the user?
    .filter(
      (formElement) => formElement.value === '' || formElement.value === '0'
    )
    .map((formElement) => [formElement, elementHistory[formElement.name]])
    .filter(
      ([_, previousElementValue]) => typeof previousElementValue === 'string'
    )
    .forEach(([formElement, previousElementValue]) => {
      formElement.value = previousElementValue;
    });
};

/**
 * Creates an event handler that will call {@link jump}, when the event is triggered in a <select> element.
 */
const createJumpFormElementListener =
  (jump: (uri: string) => void): EventListenerOrEventListenerObject =>
  (e) => {
    if (!(e.target instanceof HTMLSelectElement)) {
      return;
    }

    [...e.target.selectedOptions]
      .map((option) => option.dataset['uri'])
      .filter(isTruthy)
      .forEach((uri) => jump(uri));
  };

/**
 * Creates a jump-function to jump to a specific uri
 * using this form as a base to jump from.
 */
const createJump =
  (formElements: FormElementNodeList, listIdentifier: string) =>
  (uri: string) => {
    const jumpToUrl = new URL(uri, window.location.href);
    const jumpItemsState = collectStateFromJumpItems(formElements);

    // save state for current list (for page back without bf-cache for example)
    window.history.replaceState(
      extendedState(listIdentifier, jumpItemsState),
      '',
      window.location.href
    );

    // set state for new list
    window.history.pushState(
      extendedState(
        createListIdentifierFromElementId(
          jumpToUrl.hash.length > 1 ? jumpToUrl.hash.substring(1) : undefined
        ),
        jumpItemsState,
        {}
      ),
      '',
      jumpToUrl.href
    );
    window.location.reload();
  };

/**
 * Register the listeners for the jumps to uri of the selected option
 */
const registerJumpListeners = (
  personList: HTMLElement,
  formElements: FormElementNodeList,
  listIdentifier: string
) => {
  const jumpElements = personList.querySelectorAll<HTMLSelectElement>(
    'select[data-is-jump-element]'
  );

  // Since we have pushed the entry onto the history stack on the previous page,
  // we need to reload the page ourselves if the user navigates through the history.
  window.addEventListener('popstate', (e) => {
    window.location.reload();
  });

  for (const jumpElement of jumpElements) {
    jumpElement.addEventListener(
      FilterSelectEvent.EVENT_TYPE,
      createJumpFormElementListener(createJump(formElements, listIdentifier))
    );

    const jumpElementMutationObserver = new MutationObserver((mutations) => {
      mutations
        .filter((mutation) => mutation.type === 'childList')
        .flatMap((mutations) => [...mutations.removedNodes])
        .filter(
          (removedNode): removedNode is HTMLOptionElement =>
            removedNode instanceof HTMLOptionElement
        )
        .filter((removedNode) => removedNode.selected)
        .forEach(() => jumpElement.dispatchEvent(new FilterSelectEvent()));
    });

    jumpElementMutationObserver.observe(jumpElement, {
      childList: true,
    });
  }
};

/**
 * Collect the values of specific {@link formElements} and write them into a map.
 */
const collectStateFromJumpItems = (formElements: FormElementNodeList) => {
  const elementsWithStateToPreserve = [...formElements].filter(
    (formElement) => !formElement.dataset['isJumpElement']
  );

  const formValues: Record<string, string> = {};

  for (const formElement of elementsWithStateToPreserve) {
    if (formElement.name in formValues) {
      throw new Error(
        `Found multiple form elements with name ${formElement.name} in a single list. `
      );
    }

    formValues[formElement.name] = formElement.value;
  }

  return formValues;
};

/**
 * Build the identifier for the history state access from a content element DOM id.
 * @param elementId
 */
const createListIdentifierFromElementId = (elementId?: string) =>
  elementId !== '' && elementId?.startsWith('c') && elementId?.length > 1
    ? elementId.substring(1)
    : 'global';

/**
 * In a jump person list,
 * there are jump filters that will cause a page navigation instead of filtering.
 */
const initializeJumpPersonList = (personList: HTMLElement) => {
  const listIdentifier = createListIdentifierFromElementId(
    personList.closest('[id^=c]')?.id
  );
  const formElements = personList.querySelectorAll<
    HTMLSelectElement | HTMLInputElement
  >('select,input');

  restoreStateFromHistory(formElements, listIdentifier);
  registerJumpListeners(personList, formElements, listIdentifier);
};

document
  .querySelectorAll<HTMLDivElement>('div.person-list--with-jumps')
  .forEach((e) => {
    try {
      initializeJumpPersonList(e);
    } catch (e) {
      console.error('Failed to initialize a jump person list', e);
    }
  });
